import {
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { LoginArea, LoginAreaProps, LoginAreaVariant } from "./login-area";
import { useMediaQuery } from "@mui/material";
import { useKeycloak } from "@react-keycloak/ssr";
import { KeycloakInstance } from "keycloak-js";
import { usePortalAuth } from "../../providers/keycloak-provider";
import { usePortalProvider } from "../../providers/portal-provider";
import { loadUserInfo } from "../../util/load-user-info";

type LoginAreaContainerProps = {
  variant?: LoginAreaVariant;
};

export const LoginAreaContainer = ({
  variant = "white",
}: LoginAreaContainerProps) => {
  const { keycloak } = useKeycloak<KeycloakInstance>();
  const { login, logout } = usePortalAuth();
  const { accessToken, env } = usePortalProvider();
  const effectiveToken = keycloak?.token || accessToken;
  const [isLoggedIn, setLoggedIn] = useState(false);
  const [userInfo, setUserInfo] = useState<Record<string, any> | null>(null);
  const isMobile = useMediaQuery("(max-width: 991px)");

  const onClickDocuments = useCallback(() => {
    document.location.href = "/app/app-documentos";
  }, []);

  const onClickSolicitations = useCallback(() => {
    document.location.href = "/app/acompanhar-minhas-solicitacoes";
  }, []);

  const onClickLogin = useCallback(() => {
    login();
  }, [login]);

  const onClickLogout = useCallback(() => {
    logout();
  }, [logout]);

  const onClickAccount = useCallback(() => {
    document.location.href = "/app/app-meus-perfis";
  }, [keycloak]);

  const userName = useMemo(() => {
    if (!userInfo?.name || !effectiveToken) {
      return;
    }
    const parts = (userInfo.name as string).split(" ");
    if (parts.length > 1) {
      return `${parts[0]} ${parts[parts.length - 1]}`;
    }

    return userInfo.name;
  }, [userInfo?.name, effectiveToken]);

  const userLevelRole = useMemo(() => {
    if (!userInfo?.name || !effectiveToken) {
      return;
    }

    if (userInfo?.roles.some((role: string) => role === "opala"))
      return "opala";
    if (userInfo?.roles.some((role: string) => role === "ouro")) return "ouro";
    if (userInfo?.roles.some((role: string) => role === "prata"))
      return "prata";
    if (userInfo?.roles.some((role: string) => role === "bronze"))
      return "bronze";
  }, [userInfo, effectiveToken]);

  useEffect(() => {
    if (effectiveToken) {
      setLoggedIn(true);
      return;
    }
    setLoggedIn(false);
  }, [effectiveToken, userInfo]);

  useEffect(() => {
    if (
      effectiveToken &&
      env?.SMARTPASS_LOGIN_URL &&
      env?.SMARTPASS_LOGIN_REALM
    )
      loadUserInfo(
        effectiveToken,
        env.SMARTPASS_LOGIN_URL,
        env.SMARTPASS_LOGIN_REALM
      )
        .then((profile) => {
          setUserInfo(profile);
        })
        .catch((e) => {
          // do nothing
        });
  }, [effectiveToken, env?.SMARTPASS_LOGIN_URL, env?.SMARTPASS_LOGIN_REALM]);

  const props: LoginAreaProps = {
    isLoggedIn,
    userName,
    onClickLogin,
    onClickAccount,
    onClickLogout,
    userLevelRole,
    onClickDocuments,
    onClickSolicitations,
    variant,
    isMobile,
  };

  return createElement(LoginArea, props);
};
